import { ref, onMounted, watch } from "@vue/composition-api";
import { axisBottom, axisLeft, select } from "d3";
import TickLabelFormatService from "../../services/TickLabelFormatService";
import { DefaultConfigs, IPadProConfigs } from "../../services/DefaultConfigs";

function getTicks(high, low, inc, count) {
  if (low + inc * count < high) {
    count++;
    return getTicks(high, low, inc, count);
  }

  return count;
}

export default function useLineChartAxes(xRef, yRef, configRef, xAxisGroupRef, yAxisGroupRef, widthRef, heightRef, isIPadProRef) {
  const lowestY = ref(0);
  const xAxis = axisBottom();
  const yAxis = axisLeft();

  const updateAxes = () => {
    const xAxisConfig = configRef?.value?.xAxis;
    const yAxisConfig = configRef?.value?.yAxis;
    if (!xAxisConfig || !yAxisConfig || !xRef?.value || !yRef?.value) {
      return;
    }

    const tickCalc = getTicks(yAxisConfig.high, yAxisConfig.low, yAxisConfig.inc, 1);
    const tickFormat = (domain) => xAxisConfig.overrides?.[domain] ?? domain;
    const tickSize = xAxisConfig?.tickSize ?? DefaultConfigs.TickSize;

    yAxis.scale(yRef.value)
      .tickSize(tickSize)
      .ticks(tickCalc);

    xAxis.scale(xRef.value)
      .tickSize(tickSize)
      .tickFormat(tickFormat);

    lowestY.value = yRef.value(yAxisConfig.low);

    select(xAxisGroupRef.value)
      .call(xAxis)
      .selectAll(".tick text")
        .style("font", isIPadProRef?.value ? IPadProConfigs.XAxisTicks.font : DefaultConfigs.XAxisTicks.font)
        .style("fill", DefaultConfigs.XAxisTicks.textColor)
        .call(TickLabelFormatService.postFormatTickLabel, tickSize);

    select(yAxisGroupRef.value)
      .call(yAxis)
      .selectAll(".tick text")
        .style("font", isIPadProRef?.value ? IPadProConfigs.YAxisTicks.font : DefaultConfigs.YAxisTicks.font)
        .style("fill", DefaultConfigs.YAxisTicks.textColor);
  };

  // --------------------------------------------------------------------------------
  // Tricky technique
  // --------------------------------------------------------------------------------
  // `requestAnimationFrame` schedules the code to be executed at the right time
  // by the browser's rendering pipeline. Without it, the axes update happen a frame
  // before the expected time, and they react
  // us to convert colors from names like 'orange' to its hexadecimal representation
  // --------------------------------------------------------------------------------
  const updateAxesOnAnimationFrame = () => requestAnimationFrame(updateAxes);

  watch([configRef, xAxisGroupRef, yAxisGroupRef, widthRef, heightRef, xRef, yRef, isIPadProRef], updateAxesOnAnimationFrame);

  onMounted(updateAxesOnAnimationFrame);

  return { lowestY };
}
